import { format } from 'date-fns'
import { t as tt } from 'i18next'
import type * as React from 'react'
import * as Api from 'src/api'
import type * as base from 'src/api/base'

export function isPlainLeftClick(e: React.MouseEvent<any>): boolean {
  return e.button === 0 && !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey
}

export interface ActionProps {
  readonly onClick: (arg0: React.MouseEvent<any>) => void
  readonly href: string
}

export function actionProps(fn: (arg0: React.MouseEvent<any>) => unknown): ActionProps {
  return {
    onClick: (e) => {
      if (isPlainLeftClick(e)) {
        e.preventDefault()

        fn(e)
      }
    },
    href: '',
  }
}

interface Page {
  readonly kind: 'firstPage' | 'lastPage' | 'currentPage' | 'page' | 'dots'
  readonly page: number
}

export function pagination(currentPage: number, totalPages: number, width: number): readonly Page[] {
  const links: Page[] = []
  const offset = width < 380 ? 1 : 3

  if (currentPage - offset > 1) {
    links.push({
      kind: 'firstPage',
      page: 1,
    })
  }

  if (currentPage - (offset + 1) > 2) {
    links.push({
      kind: 'dots',
      page: 0,
    })
  }

  for (
    let i = Math.max(
      1,
      currentPage - (offset + (currentPage > offset + 2 && currentPage - offset < offset + 1 ? 1 : 0))
    );
    i < currentPage;
    i++
  ) {
    links.push({
      kind: 'page',
      page: i,
    })
  }

  links.push({
    kind: 'currentPage',
    page: currentPage,
  })

  if (currentPage < totalPages + 1) {
    for (let i = currentPage + 1; i < Math.min(currentPage + (offset + 1), totalPages); i++) {
      links.push({
        kind: 'page',
        page: i,
      })
    }
  }

  if (currentPage < totalPages - (offset + 1)) {
    links.push({
      kind: 'dots',
      page: totalPages + 1,
    })
  }

  if (currentPage < totalPages) {
    links.push({
      kind: 'lastPage',
      page: totalPages,
    })
  }

  return links
}

export type QueryValue = null | string | number | boolean | QueryArray | QueryObject
export type QueryArray = readonly QueryValue[]
export interface QueryObject {
  readonly [key: string]: QueryValue
}

function isQueryArray(queryValue: QueryValue): queryValue is QueryArray {
  return Array.isArray(queryValue)
}
function isQueryObject(queryValue: QueryValue): queryValue is QueryObject {
  return queryValue != null && typeof queryValue === 'object'
}

export function queryToString(queryObject: QueryObject): string {
  const values = Object.entries(queryObject).flatMap(([i, queryValue]) => {
    if (queryValue == null) {
      return []
    } else {
      return QueryValueToString(i, queryValue)
    }
  })

  if (values.length > 0) {
    return `?${values.join('&')}`
  }

  return ''
}

function QueryObjectToString(key: string, queryObject: QueryObject): string {
  const values = Object.entries(queryObject).flatMap(([i, queryValue]) => {
    if (queryValue == null) {
      return []
    } else {
      return QueryValueToString(`${key}[${i}]`, queryValue)
    }
  })

  return values.join('&')
}

function QueryValueToString(key: string, queryValue: Exclude<QueryValue, null>): string {
  if (isQueryArray(queryValue)) {
    return QueryArrayToString(key, queryValue)
  } else if (isQueryObject(queryValue)) {
    return QueryObjectToString(key, queryValue)
  } else {
    return `${key}=${queryValue.toString()}`
  }
}

function QueryArrayToString(key: string, queryArray: QueryArray): string {
  const values = queryArray.flatMap((queryValue, i) => {
    if (queryValue == null) {
      return []
    } else {
      return QueryValueToString(`${key}[${i.toString()}]`, queryValue)
    }
  })

  return values.join('&')
}

export type FormDataValue = undefined | null | string | number | Blob

/** multipart/form-data body; `null` and `undefined` fields are left out. */
export function toFormData(fields: { readonly [key: string]: FormDataValue }): FormData {
  const formData = new FormData()

  for (const [key, value] of Object.entries(fields)) {
    if (value != null) {
      formData.append(key, value instanceof Blob ? value : value.toString())
    }
  }

  return formData
}

const byteUnits = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] as const

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  let i = -1
  do {
    bytes = bytes / 1024
    i++
  } while (bytes > 1024)

  return Math.max(bytes, 0.1).toFixed(1) + ' ' + byteUnits[i]!
}

export type Assignable<A, B> = A extends B ? true : false
export type Interchangeable<A, B> = A extends B ? (B extends A ? true : false) : false

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function staticAssert<A extends true>(): void {}

export function identity<T>(value: T): T {
  return value
}

export function errorsToArray(obj: { readonly [key: string]: readonly string[] } | undefined): readonly string[] {
  const errors: string[] = []
  if (obj == null) return []
  for (const value in obj) {
    errors.push(...obj[value]!)
  }
  return errors
}

export function truncateString(str: string = '', maxNum: number | undefined = 20): string {
  return str.length > maxNum ? str.slice(0, maxNum) + '...' : str
}

export function errorMessage(error: base.CommonError): string {
  if (error.type === 'ErrorsObject') {
    const generalFirst = error.errors.general?.[0]
    if (generalFirst != null) {
      return generalFirst
    }
    const fromFields = errorsToArray(error.errors)
    if (fromFields.length > 0) {
      return fromFields.join(' ')
    }
    return tt('error:an_error_occurred')
  }
  return error.message
}

export const formatTelNumber = (str: string, type: 'home' | 'mobile' = 'mobile'): string => {
  const numericInput = str.replace(/\D/g, '').slice(0, type === 'home' ? 10 : 9)
  const firstThreeDigits = numericInput.slice(0, 3)
  const groupedDigits =
    type === 'home'
      ? [...(numericInput.slice(3, 6).match(/.{1,3}/g) ?? []), ...(numericInput.slice(6).match(/.{1,2}/g) ?? [])]
      : (numericInput.slice(3).match(/.{1,2}/g) ?? [])
  return `${numericInput.length > 3 ? `(${firstThreeDigits}) ` : firstThreeDigits}${groupedDigits.join(' ')}`
}

export function cacheValidation<T>(asyncValidate: (val: T) => Promise<boolean>): (val: T) => Promise<boolean> {
  let _valid = false
  let _value: any

  return async (value) => {
    if (value == null && _value == null) return true
    if (value !== _value) {
      const response = await asyncValidate(value)
      _value = value
      _valid = response
      return response
    }
    return _valid
  }
}

const active = new Map<string, Promise<any>>()

export async function inFlightReUse<T>(unique: string, doFly: () => Promise<T>): Promise<T> {
  if (!active.has(unique)) {
    const result = doFly()
    active.set(unique, result)

    void result.finally(() => active.delete(unique))
  }

  return await active.get(unique)
}

export function formatDate(date: Date | string | null): string | null {
  if (date != null) {
    if (typeof date === 'string') {
      return date
    } else {
      return format(date, 'dd/MM/yyyy')
    }
  }
  return null
}

export function formatDatelocale(value: string | Date | null): Date | string | null {
  if (typeof value === 'string' && value !== '') {
    const parts = value.split(/\/|-/)
    const day = parseInt(parts[0] ?? '', 10)
    const month = parseInt(parts[1] ?? '', 10) - 1 // mnths are 0-indexed (0-11)
    const year = parseInt(parts[2] ?? '', 10)

    return new Date(year, month, day)
  }
  return value
}
export function checkYoutubeLink(url: string): boolean {
  return /^https?:\/\/(www\.)?youtu(\.be|be\.com)\//.test(url)
}
export function checkVimeoLink(url: string): boolean {
  return /^https?:\/\/(www\.)?vimeo.com\//.test(url)
}
export function getStudentProgramByDegree(studentDegree?: Api.UserProfile['degree']): string {
  switch (studentDegree) {
    case 'STUDENT_BACHELOR':
      return tt('degrees:STUDENT_BACHELOR')
    case 'STUDENT_BACHELOR_MASTER':
      return tt('degrees:STUDENT_BACHELOR_MASTER')
    case 'STUDENT_DOCTOR':
      return tt('degrees:STUDENT_DOCTOR')
    case 'STUDENT_FORCERTIFICATE':
      return tt('degrees:STUDENT_FORCERTIFICATE')
    case 'STUDENT_FREE':
      return tt('degrees:STUDENT_FREE')
    case 'STUDENT_PREPARATIONAL':
      return tt('degrees:STUDENT_PREPARATIONAL')
    case 'STUDENT_MASTER':
      return tt('degrees:STUDENT_MASTER')
    case 'STUDENT_ONE_STAGE_HIGHER':
      return tt('degrees:STUDENT_ONE_STAGE_HIGHER')
    case 'STUDENT_ONE_STAGE_HIGHER_5_YEARS':
      return tt('degrees:STUDENT_ONE_STAGE_HIGHER_5_YEARS')
    case 'STUDENT_ONE_STAGE_HIGHER_4_YEARS':
      return tt('degrees:STUDENT_ONE_STAGE_HIGHER_4_YEARS')
    case 'STUDENT_ONE_STAGE_HIGHER_6_YEARS':
      return tt('degrees:STUDENT_ONE_STAGE_HIGHER_6_YEARS')
    case 'STUDENT_PROFESSIONAL':
      return tt('degrees:STUDENT_PROFESSIONAL')
    case 'STUDENT_PROFESSIONAL_3':
      return tt('degrees:STUDENT_PROFESSIONAL_3')
    case 'STUDENT_PROFESSIONAL_4':
      return tt('degrees:STUDENT_PROFESSIONAL_4')
    case 'STUDENT_PROFESSIONAL_5':
      return tt('degrees:STUDENT_PROFESSIONAL_5')
    default:
      return tt('degrees:STUDENT_BACHELOR')
  }
}
export function getStudentCardProgramByDegree(studentDegree?: Api.UserProfile['degree']): string {
  switch (studentDegree) {
    case 'STUDENT_BACHELOR':
      return tt('program:STUDENT_BACHELOR_PROGRAM')
    case 'STUDENT_BACHELOR_MASTER':
      return tt('program:STUDENT_BACHELOR_MASTER_PROGRAM')
    case 'STUDENT_DOCTOR':
      return tt('program:STUDENT_DOCTOR_PROGRAM')
    case 'STUDENT_FORCERTIFICATE':
      return tt('program:STUDENT_FORCERTIFICATE_PROGRAM')
    case 'STUDENT_FREE':
      return tt('degrees:STUDENT_FREE')
    case 'STUDENT_PREPARATIONAL':
      return tt('degrees:STUDENT_PREPARATIONAL')
    case 'STUDENT_MASTER':
      return tt('program:STUDENT_MASTER_PROGRAM')
    case 'STUDENT_ONE_STAGE_HIGHER':
      return tt('degrees:STUDENT_ONE_STAGE_HIGHER')
    case 'STUDENT_ONE_STAGE_HIGHER_5_YEARS':
      return tt('degrees:STUDENT_ONE_STAGE_HIGHER_5_YEARS')
    case 'STUDENT_ONE_STAGE_HIGHER_4_YEARS':
      return tt('degrees:STUDENT_ONE_STAGE_HIGHER_4_YEARS')
    case 'STUDENT_ONE_STAGE_HIGHER_6_YEARS':
      return tt('degrees:STUDENT_ONE_STAGE_HIGHER_6_YEARS')
    case 'STUDENT_PROFESSIONAL':
      return tt('program:STUDENT_PROFESSIONAL_PROGRAM')
    case 'STUDENT_PROFESSIONAL_3':
      return tt('degrees:STUDENT_PROFESSIONAL_3')
    case 'STUDENT_PROFESSIONAL_4':
      return tt('degrees:STUDENT_PROFESSIONAL_4')
    case 'STUDENT_PROFESSIONAL_5':
      return tt('degrees:STUDENT_PROFESSIONAL_5')
    default:
      return tt('program:STUDENT_BACHELOR_PROGRAM')
  }
}
export function isNumber(value: string): RegExpMatchArray | null {
  return value.match(/^-?[0-9]*\.?[0-9]*$/)
}

export function getStudentDegreeOptions(): readonly { value: string; label: string }[] {
  return [
    { value: '1', label: tt('degrees:STUDENT_BACHELOR') },
    { value: '11', label: tt('degrees:STUDENT_BACHELOR_MASTER') },
    { value: '3', label: tt('degrees:STUDENT_DOCTOR') },
    { value: '10', label: tt('degrees:STUDENT_FORCERTIFICATE') },
    { value: '6', label: tt('degrees:STUDENT_FREE') },
    { value: '5', label: tt('degrees:STUDENT_PREPARATIONAL') },
    { value: '2', label: tt('degrees:STUDENT_MASTER') },
    { value: '12', label: tt('degrees:STUDENT_ONE_STAGE_HIGHER') },
    { value: '13', label: tt('degrees:STUDENT_ONE_STAGE_HIGHER_5_YEARS') },
    { value: '14', label: tt('degrees:STUDENT_ONE_STAGE_HIGHER_4_YEARS') },
    { value: '15', label: tt('degrees:STUDENT_ONE_STAGE_HIGHER_6_YEARS') },
    { value: '4', label: tt('degrees:STUDENT_PROFESSIONAL') },
    { value: '7', label: tt('degrees:STUDENT_PROFESSIONAL_3') },
    { value: '8', label: tt('degrees:STUDENT_PROFESSIONAL_4') },
    { value: '9', label: tt('degrees:STUDENT_PROFESSIONAL_5') },
  ]
}

export function getAdminDegreeName(adminDegree?: Api.AdministratorDegree): string {
  switch (adminDegree) {
    case 'ADMINISTRATOR_FACULTY_ADMINISTRATOR':
      return tt('degrees:ADMINISTRATOR_FACULTY_ADMINISTRATOR')
    case 'ADMINISTRATOR_FACULTY_MODERATOR':
      return tt('degrees:ADMINISTRATOR_FACULTY_MODERATOR')
    case 'ADMINISTRATOR_REGISTRAR':
      return tt('degrees:ADMINISTRATOR_REGISTRAR')
    case 'ADMINISTRATOR_MAIN_ADMINISTRATOR':
      return tt('degrees:ADMINISTRATOR_MAIN_ADMINISTRATOR')
    case 'SURVEY_ADMINISTRATOR':
      return tt('degrees:SURVEY_ADMINISTRATOR')
    default:
      return tt('degrees:ADMINISTRATOR_FACULTY_ADMINISTRATOR')
  }
}

export function getLecturerDegreeName(lecturerDegree?: Api.LecturerDegree): string {
  switch (lecturerDegree) {
    case 'LECTURER_PROFESSOR':
      return tt('degrees:LECTURER_PROFESSOR')
    case 'LECTURER_ASSOC_PROFESSOR':
      return tt('degrees:LECTURER_ASSOC_PROFESSOR')
    case 'LECTURER_ASSISTANT_PROFESSOR':
      return tt('degrees:LECTURER_ASSISTANT_PROFESSOR')
    case 'LECTURER_INVITED_LECTURER':
      return tt('degrees:LECTURER_INVITED_LECTURER')
    case 'LECTURER_PROFESSOR_EMERITUS':
      return tt('degrees:LECTURER_PROFESSOR_EMERITUS')
    case 'LECTURER_RESP_PROFESSOR':
      return tt('degrees:LECTURER_RESP_PROFESSOR')
    case 'LECTURER_ASSISTANT':
      return tt('degrees:LECTURER_ASSISTANT')
    case 'LECTURER_COURSE_CURATOR':
      return tt('degrees:LECTURER_COURSE_CURATOR')
    case 'LECTURER_ACADEMIC_PERSONAL':
      return tt('degrees:LECTURER_ACADEMIC_PERSONAL')
    case 'LECTURER_TEACHER':
      return tt('degrees:LECTURER_TEACHER')
    default:
      return tt('degrees:LECTURER_PROFESSOR')
  }
}

export function countWords(text: string | undefined | null): number {
  if (!text || text.trim() === '') return 0
  return text.trim().split(/\s+/).length
}

export function getCountPlain(value: string | undefined | null, isWordMode: boolean): number {
  return isWordMode ? countWords(value) : (value?.length ?? 0)
}

export function getTextFromHtml(html: string | undefined | null): string {
  if (html == null || html === '') return ''
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function getCountFromHtml(html: string | undefined | null, isWordMode: boolean): number {
  const text = getTextFromHtml(html)
  return isWordMode ? countWords(text) : text.length
}

/**
 * Legacy program and syllabus HTML was pasted out of MS Word and stored with its
 * conditional-comment markers entity-escaped: `&lt;!-- [if !supportLists]--&gt;`.
 * Old Argus printed that string raw inside a `<textarea>`, and textarea content is
 * RCDATA, so the browser decoded the entities back into real comments before TinyMCE
 * ever parsed them — they rendered as nothing. Here the value reaches TinyMCE as a JS
 * string with no such decoding step, so the markers surface as literal text in the
 * editor. Dropping them reproduces what the old page showed.
 *
 * Only these markers are touched, never the surrounding markup: decoding the whole
 * string would turn any deliberately escaped tag into live markup. Apply to values
 * arriving from the API, not to the editor's own output.
 */
export function stripEscapedWordComments(html: string): string {
  return html.replace(/&lt;!--[\s\S]*?--&gt;/g, '')
}

/**
 * The API marks a value it could not translate into the requested language with a
 * leading `[GEO]` followed by the Georgian text. When the Georgian side is empty too
 * — a program with no direction, say — only the marker comes back, so the English
 * page prints a bare `[GEO]` where the Georgian one prints nothing. Treat such a
 * value as missing; a marked value that does carry text is returned untouched.
 */
export function blankTranslationToNull(value: string | null | undefined): string | null {
  const text = value?.trim() ?? ''
  return text.replace(/^\[GEO\]/, '').trim() === '' ? null : text
}

/**
 * Drops the `[GEO]` marker wherever it sits, for values that are never really translated —
 * a person's name, say, where the API composes first and last name and marks only the half
 * it has no English for (`"test1 [GEO] "`). Unlike {@link blankTranslationToNull}, which keeps
 * the marker so an admin can see a field is untranslated, this is for values where the marker
 * carries no information and only disfigures the text. Returns null when nothing is left.
 */
export function stripTranslationMarkers(value: string | null | undefined): string | null {
  const text = (value ?? '')
    .replace(/\[GEO\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return text === '' ? null : text
}
export type TranslateFn = (key: any, en?: any) => string
export type TranslatePairFn = (pair: [ka: string, en: string]) => string
export function getRequiredError(options: {
  required: boolean
  label: string | undefined
  t: TranslateFn
}): string | undefined {
  const { required, label, t } = options
  if (!required) return undefined
  return label != null ? `${label} ${t('common:is_required')}` : t('common:field_is_required')
}

export function getMinMaxValidationError(options: {
  count: number
  minLength: number | undefined
  maxLength: number | undefined
  isWordMode: boolean
  t: TranslatePairFn
}): string | undefined {
  const { count, minLength, maxLength, isWordMode, t } = options
  if (minLength != null && count < minLength) {
    return isWordMode
      ? t([`მინიმუმ ${minLength} სიტყვა`, `Minimum ${minLength} words`])
      : t([`მინიმუმ ${minLength} სიმბოლო`, `Minimum ${minLength} characters`])
  }
  if (maxLength != null && count > maxLength) {
    return isWordMode
      ? t([`მაქსიმუმ ${maxLength} სიტყვა`, `Maximum ${maxLength} words`])
      : t([`მაქსიმუმ ${maxLength} სიმბოლო`, `Maximum ${maxLength} characters`])
  }
  return undefined
}

export function getRangeHint(options: {
  minLength: number | undefined
  maxLength: number | undefined
  isWordMode: boolean
  t: TranslatePairFn
}): string | null {
  const { minLength, maxLength, isWordMode, t } = options
  const hasMin = minLength != null
  const hasMax = maxLength != null
  if (!hasMin && !hasMax) return null
  if (hasMin && hasMax) {
    return isWordMode
      ? t([`${minLength}–${maxLength} სიტყვა`, `${minLength}–${maxLength} words`])
      : t([`${minLength}–${maxLength} სიმბოლო`, `${minLength}–${maxLength} characters`])
  }
  if (hasMax) {
    return isWordMode
      ? t([`მაქსიმუმ ${maxLength} სიტყვა`, `Max ${maxLength} words`])
      : t([`მაქსიმუმ ${maxLength} სიმბოლო`, `Max ${maxLength} characters`])
  }
  return isWordMode
    ? t([`მინიმუმ ${minLength} სიტყვა`, `Min ${minLength} words`])
    : t([`მინიმუმ ${minLength} სიმბოლო`, `Min ${minLength} characters`])
}

/** Counter suffix for FormikTinyMce: " / (min.X) - (max.X)" | " / (max.X)" | " (min X words)" */
export function getCounterSuffix(options: {
  minLength: number | undefined
  maxLength: number | undefined
  isWordMode: boolean
  t: TranslatePairFn
}): string {
  const { minLength, maxLength, isWordMode, t } = options
  if (maxLength != null && minLength != null) {
    return ` / (${t([`მინ.${minLength}`, `min.${minLength}`])}) - (${t([`მაქს.${maxLength}`, `max.${maxLength}`])})`
  }
  if (maxLength != null) {
    return ` / (${t([`მაქს.${maxLength}`, `max.${maxLength}`])})`
  }
  if (minLength != null) {
    const minLabel = isWordMode
      ? t([`მინ. ${minLength} სიტყვა`, `min ${minLength} words`])
      : t([`მინ. ${minLength} სიმბოლო`, `min ${minLength} characters`])
    return ` (${minLabel})`
  }
  return ''
}
